import React, { Fragment, Suspense, useEffect } from "react";
import { BrowserRouter, Redirect, Switch } from "react-router-dom";
import AuthenticationWrap from "./views/controls/auth/AuthenticationWrap";
import PrivateRoute from "./views/controls/auth/PrivateRoute";
import CommonComponentsWrap from "./views/controls/common/CommonComponentsWrap";
import PublicComponentsWrap from "./views/controls/public/PublicComponentsWrap";
import TransformRoute from "./views/controls/components/transformRoute/TransformRoute";
import { GlobalStyleWrap } from "./views/controls/GlobalStyleWrap";
import LayoutWrap from "./views/controls/layout/LayoutWrap";
import ReactGA from "react-ga4";
import environments from "./environment";
import DashBoard from "./views/pages/dashboard/DashBoard";
import MyBookingPage from "./views/pages/MyBooking/MyBooking";
import UserManagementPage from "./views/pages/User/Usermanagement";
import PartnerManagementPage from "./views/pages/Partner/PartnerManagement";
import PartnerBulkEditPage from "./views/pages/Partner/PartnerBulkEditPage";
import StationRequestsDashboardPage from "./views/pages/StationRequests/StationRequestsDashboardPage";
import PaymentChannelNumberPage from "./views/pages/AdminPaymentChannelNumber/PaymentChannelNumber";
import AdminContentManage from "./views/pages/AdminContentManage/AdminContentManage";
import TextWorkspace from "./views/pages/TextWorkspace";
import AdminTextDomBridge from "./views/controls/components/adminText/AdminTextDomBridge";
import AdminTopDestinationManage from "./views/pages/TopDestinationManage/TopDestinationManage";
import AdminFormatEmail from "./views/pages/AdminFormatEmail/AdminFormatEmail";
import DebtManagement from "./views/pages/DebtManagement/DebtManagement";
import BankChangeReview from "./views/pages/BankChangeReview/BankChangeReview";
import BusinessLicences from "./views/pages/BusinessLicences/BusinessLicences";
import LegalEntities from "./views/pages/LegalEntities/LegalEntities";
import PricePlans from "./views/pages/PricePlans/PricePlans";
import PayoutBatches from "./views/pages/PayoutBatches/PayoutBatches";
import DebtDetail from "./views/pages/DebtDetail/DebtDetail";
import DebtHistoryLookup from "./views/pages/DebtHistoryLookup/DebtHistoryLookup";
import NotFound from "./views/pages/NotFound/NotFound";
import NotificationManagement from "./views/pages/NotificationManagement/NotificationManagement";
import CountryRates from "./views/pages/CountryRates/CountryRates";
import AuditLogsPage from "./views/pages/AuditLogs/AuditLogs";
import ConsentLogsPage from "./views/pages/ConsentLogs/ConsentLogs";
import StationPortalLogsPage from "./views/pages/StationPortalLogs/StationPortalLogs";
import AuditTimelinePage from "./views/pages/AuditTimeline";

import PaymentCycleSettings from "./views/pages/PaymentCycleSettings/PaymentCycleSettings";
import PaymentSurchargeSettings from "./views/pages/PaymentSurchargeSettings/PaymentSurchargeSettings";
import SystemConfigPage from "./views/pages/SystemConfig/SystemConfig";
import ServiceInterestClicksPage from "./views/pages/ServiceInterestClicks/ServiceInterestClicksPage";
import SearchTrackingPage from "./views/pages/SearchTracking/SearchTrackingPage";
import SupportCategoriesPage from "./views/pages/SupportCategories/SupportCategoriesPage";
import BagContentPage from "./views/pages/BagContent/BagContent";
import FeatureFlagsPage from "./views/pages/FeatureFlags/FeatureFlags";
import SystemHealthPage from "./views/pages/SystemHealth/SystemHealth";
// import Redirect from "./views/controls/components/Redirect";

export default function App() {
	useEffect(() => {
		const gaKey = String(environments.gaKey || "").trim();
		if (!gaKey) {
			return;
		}
		try {
			ReactGA.initialize(gaKey);
			ReactGA.send({ hitType: "pageview", page: window.location.pathname, title: window.location.pathname });
		} catch {
			// GA is optional; bad/missing network gtag must not break the admin app
			return;
		}
		const observer = new PerformanceObserver(performanceObserver);
		observer.observe({
			entryTypes: ["navigation"],
		});
		return () => observer.disconnect();
	}, []);
	const performanceObserver = (list: PerformanceObserverEntryList) => {
		const gaKey = String(environments.gaKey || "").trim();
		if (!gaKey) {
			return;
		}
		try {
			list.getEntriesByType("navigation").forEach((entry) => {
				ReactGA.gtag("config", gaKey, {
					custom_map: {
						TotalAppLoadTime: (entry as any).responseEnd - (entry as any).requestStart,
					},
				});
				ReactGA.gtag("config", gaKey, {
					custom_map: {
						DownloadTime: (entry as any).responseEnd - (entry as any).responseStart,
					},
				});
				ReactGA.gtag("config", gaKey, {
					custom_map: {
						ServerLatency: (entry as any).responseStart - (entry as any).requestStart,
					},
				});
				ReactGA.gtag("event", "PagePerformance", {
					TotalAppLoadTime: (entry as any).responseEnd - (entry as any).requestStart,
				});
				ReactGA.gtag("event", "PagePerformance", {
					DownloadTime: (entry as any).responseEnd - (entry as any).responseStart,
				});
				ReactGA.gtag("event", "PagePerformance", {
					ServerLatency: (entry as any).responseStart - (entry as any).requestStart,
				});
			});
		} catch {
			/* ignore GA performance reporting failures */
		}
	};
	return (
		<Fragment>
			<div className="app-wrapper">
				<AdminTextDomBridge />
				<CommonComponentsWrap>
					<BrowserRouter basename="/">
						<AuthenticationWrap>
							<PublicComponentsWrap>
								<GlobalStyleWrap />

								<LayoutWrap
									includePaths={[
										"/",
										"/booking",
										"/user",
										"/partner",
										"/station-requests",
										"/payment-channel-number",
										"/content-manage",
										"/stations-text",
										"/homepage-text",
										"/admin-text",
										"/top-destination-manage",
										"/format-email",
										"/debt-management",
										"/debt-detail",
										"/partner-debt-detail",
										"/debt-history",
										"/bank-change-review",
										"/business-licences",
										"/legal-entities",
										"/price-plans",
										"/payout-batches",

										"/notifications",
										"/country-rates",
										"/audit-logs",
										"/station-portal-logs",
										"/consent-logs",
										"/payment-cycle-settings",
										"/payment-surcharge-settings",
										"/system-config",
										"/service-interest-clicks",
										"/search-tracking",
										"/station-documents",
										"/partner-bulk-edit",
										"/support-categories",
										"/bag-content",
										"/feature-flags",
										"/system-health",
										"/404",
										"*",
									]}>
									<PrivateRoute requireAdmin>
										<Switch>
											<TransformRoute
												exact
												path="/">
												<Suspense fallback={<div />}>
													<DashBoard />
												</Suspense>
											</TransformRoute>
											<TransformRoute
												exact
												path="/booking">
												<Suspense fallback={<div />}>
													<MyBookingPage />
												</Suspense>
											</TransformRoute>
											<TransformRoute
												exact
												path="/user">
												<Suspense fallback={<div />}>
													<UserManagementPage />
												</Suspense>
											</TransformRoute>
											<TransformRoute
												exact
												path="/partner">
												<Suspense fallback={<div />}>
													<PartnerManagementPage />
												</Suspense>
											</TransformRoute>
											<TransformRoute
												exact
												path="/partner-bulk-edit">
												<Suspense fallback={<div />}>
													<PartnerBulkEditPage />
												</Suspense>
											</TransformRoute>
											<TransformRoute
												exact
												path="/station-requests">
												<Suspense fallback={<div />}>
													<StationRequestsDashboardPage />
												</Suspense>
											</TransformRoute>
											<TransformRoute
												exact
												path="/payment-channel-number">
												<Suspense fallback={<div />}>
													<PaymentChannelNumberPage />
												</Suspense>
											</TransformRoute>
											<TransformRoute
												exact
												path="/content-manage">
												<Suspense fallback={<div />}>
													<AdminContentManage />
												</Suspense>
											</TransformRoute>
											<TransformRoute
												exact
												path="/text-editor">
												<Suspense fallback={<div />}>
													<TextWorkspace />
												</Suspense>
											</TransformRoute>
											<TransformRoute
												exact
												path="/stations-text">
												<Redirect to="/text-editor?scope=stations" />
											</TransformRoute>
											<TransformRoute
												exact
												path="/homepage-text">
												<Redirect to="/text-editor?scope=homepage" />
											</TransformRoute>
											<TransformRoute
												exact
												path="/admin-text">
												<Redirect to="/text-editor?scope=admin" />
											</TransformRoute>
											<TransformRoute
												exact
												path="/top-destination-manage">
												<Suspense fallback={<div />}>
													<AdminTopDestinationManage />
												</Suspense>
											</TransformRoute>
											<TransformRoute
												exact
												path="/format-email">
												<Suspense fallback={<div />}>
													<AdminFormatEmail />
												</Suspense>
											</TransformRoute>
											<TransformRoute
												exact
												path="/debt-management">
												<Suspense fallback={<div />}>
													<DebtManagement />
												</Suspense>
											</TransformRoute>
											{/* Gộp lệnh Wise theo bên ký — B3 chuỗi, Quân chốt 17/10/2026 */}
											<TransformRoute
												exact
												path="/payout-batches">
												<Suspense fallback={<div />}>
													<PayoutBatches />
												</Suspense>
											</TransformRoute>
											{/* Bên ký · uỷ quyền · tên chuỗi — Quân chốt 17/10/2026 (pháp nhân làm gốc) */}
											<TransformRoute
												exact
												path="/legal-entities">
												<Suspense fallback={<div />}>
													<LegalEntities />
												</Suspense>
											</TransformRoute>
											{/* Price plans — chỉ super admin; giá chỉ đổi khi bấm Apply */}
											<TransformRoute
												exact
												path="/price-plans">
												<Suspense fallback={<div />}>
													<PricePlans />
												</Suspense>
											</TransformRoute>
											{/* Hàng chờ duyệt giấy phép kinh doanh — Quân 26/10/2026 */}
											<TransformRoute
												exact
												path="/business-licences">
												<Suspense fallback={<div />}>
													<BusinessLicences />
												</Suspense>
											</TransformRoute>
											{/* Duyệt tài khoản nhận tiền của điểm — lớp ② bộ 3 lớp Quân duyệt 11/09 */}
											<TransformRoute
												exact
												path="/bank-change-review">
												<Suspense fallback={<div />}>
													<BankChangeReview />
												</Suspense>
											</TransformRoute>
											<TransformRoute
												exact
												path="/debt-history">
												<Suspense fallback={<div />}>
													<DebtHistoryLookup />
												</Suspense>
											</TransformRoute>

											<TransformRoute
												exact
												path="/debt-detail">
												<Suspense fallback={<div />}>
													<DebtDetail />
												</Suspense>
											</TransformRoute>
											<TransformRoute
												exact
												path="/partner-debt-detail">
												<Suspense fallback={<div />}>
													<DebtDetail />
												</Suspense>
											</TransformRoute>
											<TransformRoute
												exact
												path="/notifications">
												<Suspense fallback={<div />}>
													<NotificationManagement />
												</Suspense>
											</TransformRoute>
											<TransformRoute
												exact
												path="/country-rates">
												<Suspense fallback={<div />}>
													<CountryRates />
												</Suspense>
											</TransformRoute>
											<TransformRoute
												exact
												path="/audit-logs">
												<Suspense fallback={<div />}>
													<AuditLogsPage />
												</Suspense>
											</TransformRoute>
											<TransformRoute
												exact
												path="/station-portal-logs">
												<Suspense fallback={<div />}>
													<StationPortalLogsPage />
												</Suspense>
											</TransformRoute>
											<TransformRoute
												exact
												path="/consent-logs">
												<Suspense fallback={<div />}>
													<ConsentLogsPage />
												</Suspense>
											</TransformRoute>
											<TransformRoute
												exact
												path="/audit-timeline">
												<Suspense fallback={<div />}>
													<AuditTimelinePage />
												</Suspense>
											</TransformRoute>
											<TransformRoute
												exact
												path="/payment-cycle-settings">
												<Suspense fallback={<div />}>
													<PaymentCycleSettings />
												</Suspense>
											</TransformRoute>
											<TransformRoute
												exact
												path="/payment-surcharge-settings">
												<Suspense fallback={<div />}>
													<PaymentSurchargeSettings />
												</Suspense>
											</TransformRoute>
											<TransformRoute
												exact
												path="/system-config">
												<Suspense fallback={<div />}>
													<SystemConfigPage />
												</Suspense>
											</TransformRoute>
											<TransformRoute
												exact
												path="/service-interest-clicks">
												<Suspense fallback={<div />}>
													<ServiceInterestClicksPage />
												</Suspense>
											</TransformRoute>
											<TransformRoute
												exact
												path="/search-tracking">
												<Suspense fallback={<div />}>
													<SearchTrackingPage />
												</Suspense>
											</TransformRoute>
											<TransformRoute
												exact
												path="/support-categories">
												<Suspense fallback={<div />}>
													<SupportCategoriesPage />
												</Suspense>
											</TransformRoute>
											<TransformRoute
												exact
												path="/bag-content">
												<Suspense fallback={<div />}>
													<BagContentPage />
												</Suspense>
											</TransformRoute>
											<TransformRoute
												exact
												path="/feature-flags">
												<Suspense fallback={<div />}>
													<FeatureFlagsPage />
												</Suspense>
											</TransformRoute>
											<TransformRoute
												exact
												path="/system-health">
												<Suspense fallback={<div />}>
													<SystemHealthPage />
												</Suspense>
											</TransformRoute>
											<TransformRoute path="*">
												<Suspense fallback={<div />}>
													<NotFound />
												</Suspense>
											</TransformRoute>
										</Switch>
									</PrivateRoute>
								</LayoutWrap>
							</PublicComponentsWrap>
						</AuthenticationWrap>
					</BrowserRouter>
				</CommonComponentsWrap>
			</div>
		</Fragment>
	);
}
